import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { useUserManagementDetail } from '@shared/hooks/api/useUserManagement';
import { useDoctorControlStore } from '@shared/stores/doctor-control-store';
import {
  getDoctorWorkspaceHomePath,
  getDoctorWorkspaceReturnPath,
  isDoctorControlPath,
  resolveDoctorWorkspacePath,
  stripDoctorControlPath,
} from '@shared/lib/doctor-workspace';
import type { UserProfile } from '@shared/types/auth';

const normalizeOrganizationRoles = (
  roles: Array<string | null | undefined>,
): NonNullable<UserProfile['organization_roles']> =>
  Array.from(
    new Set(
      roles.filter(
        (role): role is NonNullable<UserProfile['organization_roles']>[number] =>
          role === 'owner' || role === 'doctor' || role === 'staff',
      ),
    ),
  );

export const useDoctorWorkspace = () => {
  const location = useLocation();
  const { user } = useAuth();
  const activeRequest = useDoctorControlStore((state) => state.activeRequest);
  const hasActiveDelegatedDoctorControl =
    (user?.role === 'admin' || user?.role === 'super_admin') &&
    activeRequest?.status === 'active';

  const isDelegatedDoctorWorkspace =
    isDoctorControlPath(location.pathname) &&
    hasActiveDelegatedDoctorControl;
  const delegatedDoctorDetailQuery = useUserManagementDetail(
    isDelegatedDoctorWorkspace ? activeRequest?.doctorId ?? null : null,
  );

  const normalizedPathname = React.useMemo(
    () => stripDoctorControlPath(location.pathname) ?? location.pathname,
    [location.pathname],
  );

  const resolveDoctorPath = React.useCallback(
    (path: string) => resolveDoctorWorkspacePath(path, isDelegatedDoctorWorkspace),
    [isDelegatedDoctorWorkspace],
  );

  const effectiveOrganizationRoles = React.useMemo(() => {
    if (!hasActiveDelegatedDoctorControl) {
      return user?.organization_roles ?? [];
    }

    const delegatedRoles = normalizeOrganizationRoles([
      ...(delegatedDoctorDetailQuery.data?.organizations.map((organization) => organization.role) ?? []),
      delegatedDoctorDetailQuery.data?.role === 'org_owner' ? 'owner' : null,
    ]);

    return delegatedRoles;
  }, [
    delegatedDoctorDetailQuery.data?.organizations,
    delegatedDoctorDetailQuery.data?.role,
    hasActiveDelegatedDoctorControl,
    user?.organization_roles,
  ]);

  const isEffectiveOrganizationOwner =
    effectiveOrganizationRoles.includes('owner');

  return {
    activeDoctorControlRequest: activeRequest,
    isDelegatedDoctorWorkspace,
    effectiveDoctorId: isDelegatedDoctorWorkspace ? activeRequest?.doctorId ?? null : user?.as_doctor_id ?? null,
    effectiveDoctorName: isDelegatedDoctorWorkspace ? activeRequest?.doctor.fullName ?? null : null,
    effectivePortalRole: isDelegatedDoctorWorkspace ? 'doctor' : user?.role,
    effectiveOrganizationRoles,
    isEffectiveOrganizationOwner,
    normalizedPathname,
    resolveDoctorPath,
    workspaceHomePath: getDoctorWorkspaceHomePath(hasActiveDelegatedDoctorControl),
    returnPath: getDoctorWorkspaceReturnPath(user?.role),
  } as const;
};
